import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  DVIRQuestionForm,
  DVIRTemplateForm,
  GetDVIRQuestionsParams,
  GetDVIRReportsParams,
  CreateDVIRReportParams,
  RemoveQuestionFromTemplateParams,
  ChangeQuestionOrderParams,
  triplePSDatasource,
  AddQuestionToTemplateParams,
} from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { DVIRQuestion, DVIRTemplate, DVIRReport } from "../../models";
import { logout } from "../Auth/authSlice";

export interface DVIRState {
  // Questions
  getDVIRQuestions: APIResult<DVIRQuestion[]>;
  getDVIRQuestion: APIResult<DVIRQuestion>;
  createDVIRQuestion: APIResult<DVIRQuestion>;
  updateDVIRQuestion: APIResult<DVIRQuestion>;
  deleteDVIRQuestion: APIResult<string>;

  // Templates
  getDVIRTemplates: APIResult<DVIRTemplate[]>;
  getDVIRTemplate: APIResult<DVIRTemplate>;
  createDVIRTemplate: APIResult<DVIRTemplate>;
  updateDVIRTemplate: APIResult<DVIRTemplate>;
  deleteDVIRTemplate: APIResult<string>;

  // Template Questions
  addQuestionToTemplate: APIResult<boolean>;
  removeQuestionFromTemplate: APIResult<boolean>;
  changeQuestionOrder: APIResult<boolean>;

  // Reports
  getDVIRReports: APIResult<DVIRReport[]>;
  createDVIRReport: APIResult<DVIRReport>;
  deleteDVIRReport: APIResult<string>;

  // Data
  questions: DVIRQuestion[];
  templates: DVIRTemplate[];
  reports: DVIRReport[];
  selectedQuestion?: DVIRQuestion;
  selectedTemplate?: DVIRTemplate;

  // UI State
  showAddQuestionModal: boolean;
  showEditQuestionModal: boolean;
  showDeleteQuestionConfirmationModal?: DVIRQuestion;
  showAddTemplateModal: boolean;
  showEditTemplateModal: boolean;
  showDeleteTemplateConfirmationModal?: DVIRTemplate;
  showDeleteReportConfirmationModal?: DVIRReport;
  showViewReportModal?: DVIRReport;
  showAddReportModal: boolean;
  questionToRemove?: string;
  templateToRemove?: string;

  // Report Filters
  reportFilters: GetDVIRReportsParams;
}

const initialState: DVIRState = {
  // Questions
  getDVIRQuestions: emptyAPIResult(),
  getDVIRQuestion: emptyAPIResult(),
  createDVIRQuestion: emptyAPIResult(),
  updateDVIRQuestion: emptyAPIResult(),
  deleteDVIRQuestion: emptyAPIResult(),

  // Templates
  getDVIRTemplates: emptyAPIResult(),
  getDVIRTemplate: emptyAPIResult(),
  createDVIRTemplate: emptyAPIResult(),
  updateDVIRTemplate: emptyAPIResult(),
  deleteDVIRTemplate: emptyAPIResult(),

  // Template Questions
  addQuestionToTemplate: emptyAPIResult(),
  removeQuestionFromTemplate: emptyAPIResult(),
  changeQuestionOrder: emptyAPIResult(),

  // Reports
  getDVIRReports: emptyAPIResult(),
  createDVIRReport: emptyAPIResult(),
  deleteDVIRReport: emptyAPIResult(),

  // Data
  questions: [],
  templates: [],
  reports: [],

  // UI State
  showAddQuestionModal: false,
  showEditQuestionModal: false,
  showAddTemplateModal: false,
  showEditTemplateModal: false,
  showAddReportModal: false,

  // Report Filters
  reportFilters: {},
};

// Question Thunks
export const getDVIRQuestions = createAsyncThunk(
  "dvir/getDVIRQuestions",
  async (params?: GetDVIRQuestionsParams) => {
    return triplePSDatasource.getDVIRQuestions(params);
  }
);

export const getDVIRQuestion = createAsyncThunk(
  "dvir/getDVIRQuestion",
  async (id: string) => {
    return triplePSDatasource.getDVIRQuestion(id);
  }
);

export const createDVIRQuestion = createAsyncThunk(
  "dvir/createDVIRQuestion",
  async (form: DVIRQuestionForm) => {
    return triplePSDatasource.createDVIRQuestion(form);
  }
);

export const updateDVIRQuestion = createAsyncThunk(
  "dvir/updateDVIRQuestion",
  async (question: DVIRQuestion) => {
    return triplePSDatasource.updateDVIRQuestion(question);
  }
);

export const deleteDVIRQuestion = createAsyncThunk(
  "dvir/deleteDVIRQuestion",
  async (id: string) => {
    return triplePSDatasource.deleteDVIRQuestion(id);
  }
);

// Template Thunks
export const getDVIRTemplates = createAsyncThunk(
  "dvir/getDVIRTemplates",
  async () => {
    return triplePSDatasource.getDVIRTemplates();
  }
);

export const getDVIRTemplate = createAsyncThunk(
  "dvir/getDVIRTemplate",
  async (id: string) => {
    return triplePSDatasource.getDVIRTemplate(id);
  }
);

export const createDVIRTemplate = createAsyncThunk(
  "dvir/createDVIRTemplate",
  async (form: DVIRTemplateForm) => {
    return triplePSDatasource.createDVIRTemplate(form);
  }
);

export const updateDVIRTemplate = createAsyncThunk(
  "dvir/updateDVIRTemplate",
  async (template: DVIRTemplate) => {
    return triplePSDatasource.updateDVIRTemplate(template);
  }
);

export const deleteDVIRTemplate = createAsyncThunk(
  "dvir/deleteDVIRTemplate",
  async (id: string) => {
    return triplePSDatasource.deleteDVIRTemplate(id);
  }
);

// Template Question Thunks with optimistic updates
export const changeQuestionOrderOptimistic = createAsyncThunk(
  "dvir/changeQuestionOrderOptimistic",
  async (params: ChangeQuestionOrderParams, { dispatch, getState }) => {
    // Call the API
    const result = await triplePSDatasource.changeQuestionOrder(params);

    // Refetch the template to get the updated state
    const updatedTemplate = await triplePSDatasource.getDVIRTemplate(
      params.templateId
    );

    return { apiResult: result, updatedTemplate };
  }
);

export const removeQuestionFromTemplateOptimistic = createAsyncThunk(
  "dvir/removeQuestionFromTemplateOptimistic",
  async (params: RemoveQuestionFromTemplateParams, { dispatch, getState }) => {
    // Call the API
    const result = await triplePSDatasource.removeQuestionFromTemplate(params);

    // Refetch the template to get the updated state
    const updatedTemplate = await triplePSDatasource.getDVIRTemplate(
      params.templateId
    );

    return { apiResult: result, updatedTemplate };
  }
);

export const addQuestionToTemplateOptimistic = createAsyncThunk(
  "dvir/addQuestionToTemplateOptimistic",
  async (params: AddQuestionToTemplateParams, { dispatch, getState }) => {
    // Call the API
    const result = await triplePSDatasource.addQuestionToTemplate(params);

    // Refetch the template to get the updated state
    const updatedTemplate = await triplePSDatasource.getDVIRTemplate(
      params.templateId
    );

    return { apiResult: result, updatedTemplate };
  }
);

// Report Thunks
export const getDVIRReports = createAsyncThunk(
  "dvir/getDVIRReports",
  async (params?: GetDVIRReportsParams) => {
    return triplePSDatasource.getDVIRReports(params);
  }
);

export const createDVIRReport = createAsyncThunk(
  "dvir/createDVIRReport",
  async (params: CreateDVIRReportParams) => {
    return triplePSDatasource.createDVIRReport(params);
  }
);

export const deleteDVIRReport = createAsyncThunk(
  "dvir/deleteDVIRReport",
  async (id: string) => {
    return triplePSDatasource.deleteDVIRReport(id);
  }
);

export const dvirSlice = createSlice({
  name: "dvir",
  initialState,
  reducers: {
    // Question Actions
    setSelectedQuestion(state, action) {
      state.selectedQuestion = action.payload;
    },
    setQuestionToRemove(state, action) {
      state.questionToRemove = action.payload;
    },
    toggleShowAddQuestionModal(state) {
      state.showAddQuestionModal = !state.showAddQuestionModal;
    },
    toggleShowEditQuestionModal(state) {
      state.showEditQuestionModal = !state.showEditQuestionModal;
    },
    showDeleteQuestionConfirmationModal(state, action) {
      state.showDeleteQuestionConfirmationModal = action.payload;
    },
    resetShowDeleteQuestionConfirmationModal(state) {
      state.showDeleteQuestionConfirmationModal = undefined;
    },

    // Template Actions
    setSelectedTemplate(state, action) {
      state.selectedTemplate = action.payload;
    },
    setTemplateToRemove(state, action) {
      state.templateToRemove = action.payload;
    },
    toggleShowAddTemplateModal(state) {
      state.showAddTemplateModal = !state.showAddTemplateModal;
    },
    toggleShowEditTemplateModal(state) {
      state.showEditTemplateModal = !state.showEditTemplateModal;
    },
    showDeleteTemplateConfirmationModal(state, action) {
      state.showDeleteTemplateConfirmationModal = action.payload;
    },
    resetShowDeleteTemplateConfirmationModal(state) {
      state.showDeleteTemplateConfirmationModal = undefined;
    },

    // Reset API states
    resetChangeQuestionOrder(state) {
      state.changeQuestionOrder = emptyAPIResult();
    },
    resetRemoveQuestionFromTemplate(state) {
      state.removeQuestionFromTemplate = emptyAPIResult();
    },
    resetAddQuestionToTemplate(state) {
      state.addQuestionToTemplate = emptyAPIResult();
    },

    // Report Actions
    showDeleteReportConfirmationModal(state, action) {
      state.showDeleteReportConfirmationModal = action.payload;
    },
    resetShowDeleteReportConfirmationModal(state) {
      state.showDeleteReportConfirmationModal = undefined;
    },
    setShowViewReportModal(state, action) {
      state.showViewReportModal = action.payload;
    },
    resetShowViewReportModal(state) {
      state.showViewReportModal = undefined;
    },
    toggleShowAddReportModal(state) {
      state.showAddReportModal = !state.showAddReportModal;
    },
    setReportFilters(state, action) {
      state.reportFilters = action.payload;
    },
    resetReportFilters(state) {
      state.reportFilters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Question Cases
      .addCase(getDVIRQuestions.pending, (state) => {
        state.getDVIRQuestions = loadingAPIResult();
      })
      .addCase(getDVIRQuestions.fulfilled, (state, action) => {
        state.getDVIRQuestions = successAPIResult(action.payload);
        state.questions = action.payload;
      })
      .addCase(getDVIRQuestions.rejected, (state, action) => {
        state.getDVIRQuestions = errorAPIResult(action.error);
      })
      .addCase(getDVIRQuestion.pending, (state) => {
        state.getDVIRQuestion = loadingAPIResult();
      })
      .addCase(getDVIRQuestion.fulfilled, (state, action) => {
        state.getDVIRQuestion = successAPIResult(action.payload);
        state.selectedQuestion = action.payload;
      })
      .addCase(getDVIRQuestion.rejected, (state, action) => {
        state.getDVIRQuestion = errorAPIResult(action.error);
      })
      .addCase(createDVIRQuestion.pending, (state) => {
        state.createDVIRQuestion = loadingAPIResult();
      })
      .addCase(createDVIRQuestion.fulfilled, (state, action) => {
        state.createDVIRQuestion = successAPIResult(action.payload);
        state.questions.push(action.payload);
        state.showAddQuestionModal = false;
      })
      .addCase(createDVIRQuestion.rejected, (state, action) => {
        state.createDVIRQuestion = errorAPIResult(action.error);
      })
      .addCase(updateDVIRQuestion.pending, (state) => {
        state.updateDVIRQuestion = loadingAPIResult();
      })
      .addCase(updateDVIRQuestion.fulfilled, (state, action) => {
        state.updateDVIRQuestion = successAPIResult(action.payload);
        const index = state.questions.findIndex(
          (q) => q.id === action.payload.id
        );
        if (index !== -1) {
          state.questions[index] = action.payload;
        }
        state.selectedQuestion = action.payload;
        state.showEditQuestionModal = false;
      })
      .addCase(updateDVIRQuestion.rejected, (state, action) => {
        state.updateDVIRQuestion = errorAPIResult(action.error);
      })
      .addCase(deleteDVIRQuestion.pending, (state) => {
        state.deleteDVIRQuestion = loadingAPIResult();
      })
      .addCase(deleteDVIRQuestion.fulfilled, (state, action) => {
        state.deleteDVIRQuestion = successAPIResult(action.payload);
        state.questions = state.questions.filter(
          (q) => q.id !== action.payload
        );
        state.questionToRemove = undefined;
        state.showDeleteQuestionConfirmationModal = undefined;
      })
      .addCase(deleteDVIRQuestion.rejected, (state, action) => {
        state.deleteDVIRQuestion = errorAPIResult(action.error);
      })

      // Template Cases
      .addCase(getDVIRTemplates.pending, (state) => {
        state.getDVIRTemplates = loadingAPIResult();
      })
      .addCase(getDVIRTemplates.fulfilled, (state, action) => {
        state.getDVIRTemplates = successAPIResult(action.payload);
        state.templates = action.payload;
      })
      .addCase(getDVIRTemplates.rejected, (state, action) => {
        state.getDVIRTemplates = errorAPIResult(action.error);
      })
      .addCase(getDVIRTemplate.pending, (state) => {
        state.getDVIRTemplate = loadingAPIResult();
      })
      .addCase(getDVIRTemplate.fulfilled, (state, action) => {
        state.getDVIRTemplate = successAPIResult(action.payload);
        state.selectedTemplate = action.payload;
      })
      .addCase(getDVIRTemplate.rejected, (state, action) => {
        state.getDVIRTemplate = errorAPIResult(action.error);
      })
      .addCase(createDVIRTemplate.pending, (state) => {
        state.createDVIRTemplate = loadingAPIResult();
      })
      .addCase(createDVIRTemplate.fulfilled, (state, action) => {
        state.createDVIRTemplate = successAPIResult(action.payload);
        state.templates.push(action.payload);
        state.showAddTemplateModal = false;
      })
      .addCase(createDVIRTemplate.rejected, (state, action) => {
        state.createDVIRTemplate = errorAPIResult(action.error);
      })
      .addCase(updateDVIRTemplate.pending, (state) => {
        state.updateDVIRTemplate = loadingAPIResult();
      })
      .addCase(updateDVIRTemplate.fulfilled, (state, action) => {
        state.updateDVIRTemplate = successAPIResult(action.payload);
        const index = state.templates.findIndex(
          (t) => t.id === action.payload.id
        );
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
        state.selectedTemplate = action.payload;
        state.showEditTemplateModal = false;
      })
      .addCase(updateDVIRTemplate.rejected, (state, action) => {
        state.updateDVIRTemplate = errorAPIResult(action.error);
      })
      .addCase(deleteDVIRTemplate.pending, (state) => {
        state.deleteDVIRTemplate = loadingAPIResult();
      })
      .addCase(deleteDVIRTemplate.fulfilled, (state, action) => {
        state.deleteDVIRTemplate = successAPIResult(action.payload);
        state.templates = state.templates.filter(
          (t) => t.id !== action.payload
        );
        if (state.selectedTemplate?.id === action.payload) {
          state.selectedTemplate = undefined;
        }
        state.templateToRemove = undefined;
        state.showDeleteTemplateConfirmationModal = undefined;
      })
      .addCase(deleteDVIRTemplate.rejected, (state, action) => {
        state.deleteDVIRTemplate = errorAPIResult(action.error);
      })

      // Template Question Cases (optimistic)
      .addCase(addQuestionToTemplateOptimistic.pending, (state) => {
        state.addQuestionToTemplate = loadingAPIResult();
      })
      .addCase(addQuestionToTemplateOptimistic.fulfilled, (state, action) => {
        state.addQuestionToTemplate = successAPIResult(
          action.payload.apiResult
        );
        // Update the selected template with fresh data
        if (
          state.selectedTemplate &&
          action.payload.updatedTemplate.id === state.selectedTemplate.id
        ) {
          state.selectedTemplate = action.payload.updatedTemplate;
        }
      })
      .addCase(addQuestionToTemplateOptimistic.rejected, (state, action) => {
        state.addQuestionToTemplate = errorAPIResult(action.error);
      })
      .addCase(changeQuestionOrderOptimistic.pending, (state) => {
        state.changeQuestionOrder = loadingAPIResult();
      })
      .addCase(changeQuestionOrderOptimistic.fulfilled, (state, action) => {
        state.changeQuestionOrder = successAPIResult(action.payload.apiResult);
        // Update the selected template with fresh data
        if (
          state.selectedTemplate &&
          action.payload.updatedTemplate.id === state.selectedTemplate.id
        ) {
          state.selectedTemplate = action.payload.updatedTemplate;
        }
      })
      .addCase(changeQuestionOrderOptimistic.rejected, (state, action) => {
        state.changeQuestionOrder = errorAPIResult(action.error);
      })
      .addCase(removeQuestionFromTemplateOptimistic.pending, (state) => {
        state.removeQuestionFromTemplate = loadingAPIResult();
      })
      .addCase(
        removeQuestionFromTemplateOptimistic.fulfilled,
        (state, action) => {
          state.removeQuestionFromTemplate = successAPIResult(
            action.payload.apiResult
          );
          // Update the selected template with fresh data
          if (
            state.selectedTemplate &&
            action.payload.updatedTemplate.id === state.selectedTemplate.id
          ) {
            state.selectedTemplate = action.payload.updatedTemplate;
          }
        }
      )
      .addCase(
        removeQuestionFromTemplateOptimistic.rejected,
        (state, action) => {
          state.removeQuestionFromTemplate = errorAPIResult(action.error);
        }
      )

      // Report Cases
      .addCase(getDVIRReports.pending, (state) => {
        state.getDVIRReports = loadingAPIResult();
      })
      .addCase(getDVIRReports.fulfilled, (state, action) => {
        state.getDVIRReports = successAPIResult(action.payload);
        state.reports = action.payload;
      })
      .addCase(getDVIRReports.rejected, (state, action) => {
        state.getDVIRReports = errorAPIResult(action.error);
      })
      .addCase(createDVIRReport.pending, (state) => {
        state.createDVIRReport = loadingAPIResult();
      })
      .addCase(createDVIRReport.fulfilled, (state, action) => {
        state.createDVIRReport = successAPIResult(action.payload);
        state.reports.unshift(action.payload);
        state.showAddReportModal = false;
      })
      .addCase(createDVIRReport.rejected, (state, action) => {
        state.createDVIRReport = errorAPIResult(action.error);
      })
      .addCase(deleteDVIRReport.pending, (state) => {
        state.deleteDVIRReport = loadingAPIResult();
      })
      .addCase(deleteDVIRReport.fulfilled, (state, action) => {
        state.deleteDVIRReport = successAPIResult(action.payload);
        state.reports = state.reports.filter((r) => r.id !== action.payload);
        state.showDeleteReportConfirmationModal = undefined;
      })
      .addCase(deleteDVIRReport.rejected, (state, action) => {
        state.deleteDVIRReport = errorAPIResult(action.error);
      })

      // Reset on logout
      .addMatcher(isAnyOf(logout), (state) => {
        Object.assign(state, initialState);
      });
  },
});

export const {
  // Question Actions
  setSelectedQuestion,
  setQuestionToRemove,
  toggleShowAddQuestionModal,
  toggleShowEditQuestionModal,
  showDeleteQuestionConfirmationModal,
  resetShowDeleteQuestionConfirmationModal,

  // Template Actions
  setSelectedTemplate,
  setTemplateToRemove,
  toggleShowAddTemplateModal,
  toggleShowEditTemplateModal,
  showDeleteTemplateConfirmationModal,
  resetShowDeleteTemplateConfirmationModal,

  // Report Actions
  showDeleteReportConfirmationModal,
  resetShowDeleteReportConfirmationModal,
  setShowViewReportModal,
  resetShowViewReportModal,
  toggleShowAddReportModal,
  setReportFilters,
  resetReportFilters,

  // Reset API states
  resetChangeQuestionOrder,
  resetRemoveQuestionFromTemplate,
  resetAddQuestionToTemplate,
} = dvirSlice.actions;

export default dvirSlice.reducer;
